import type { CmsAdapter } from '@eneco/cms-adapter';

import type { AddressFinderContent } from './address-finder-content.types';

interface FormFieldLike {
  label?: string;
  placeholder?: string;
  hint?: string;
  requiredMessage?: string;
  validationMessage?: string;
}

interface NotificationLike {
  title?: string;
  content?: unknown;
  variant?: string;
}

const toFieldLabel = (field?: FormFieldLike) =>
  field
    ? {
        value: {
          label: field.label ?? '',
          placeholder: field.placeholder,
          hint: field.hint ?? '',
          requiredMessage: field.requiredMessage ?? '',
          validationMessage: field.validationMessage ?? '',
        },
      }
    : undefined;

const toNotificationLabel = (adapter: CmsAdapter, notification?: NotificationLike) =>
  notification
    ? {
        value: {
          title: notification.title ?? '',
          content: adapter.toRichText(notification.content) ?? '',
          variant: (notification.variant ?? 'error') as 'info' | 'success' | 'warning' | 'error' | 'chat',
        },
      }
    : undefined;

/**
 * Shared with `SmeAddressFinder`: the widget's `customLabels` shape from the resolved (role-collapsed
 * or raw Sitecore) form-field/notification content — see `address-finder-content.types.ts` for the
 * ADR-0068 role-collapse this input already accounts for.
 */
export const toAddressFinderCustomLabels = (
  adapter: CmsAdapter,
  fields: {
    houseNumberFormField?: FormFieldLike;
    houseNumberSuffixFormField?: FormFieldLike;
    postalCodeFormField?: FormFieldLike;
    notFoundErrorNotification?: NotificationLike;
    serverErrorNotification?: NotificationLike;
  },
) => ({
  houseNumber: toFieldLabel(fields.houseNumberFormField),
  houseNumberSuffix: toFieldLabel(fields.houseNumberSuffixFormField),
  postalCode: toFieldLabel(fields.postalCodeFormField),
  notFoundErrorNotification: toNotificationLabel(adapter, fields.notFoundErrorNotification),
  serverErrorNotification: toNotificationLabel(adapter, fields.serverErrorNotification),
});

/** Shared with `SmeAddressFinder`, whose content carries the same ADR-0068 role-collapse. */
export const findByRole = <T extends { role?: string }>(entries: T[] | undefined, role: string): T | undefined =>
  entries?.find(entry => entry.role === role);

/**
 * `libs/cms-sitecore`'s adapter does no ADR-0068 role-collapse, so on `?_cms=sitecore` the role arrays are
 * always empty and the raw Sitecore field names carry the data — each `??` falls back to that raw field.
 */
export const resolveAddressFinderCustomLabels = (
  adapter: CmsAdapter,
  content: Pick<
    AddressFinderContent,
    | 'formFields'
    | 'notifications'
    | 'houseNumberFormField'
    | 'houseNumberSuffixFormField'
    | 'postalCodeFormField'
    | 'notFoundErrorNotification'
    | 'serverErrorNotification'
  >,
) =>
  toAddressFinderCustomLabels(adapter, {
    houseNumberFormField: findByRole(content.formFields, 'houseNumberFormField') ?? content.houseNumberFormField,
    houseNumberSuffixFormField: findByRole(content.formFields, 'houseNumberSuffixFormField') ?? content.houseNumberSuffixFormField,
    postalCodeFormField: findByRole(content.formFields, 'postalCodeFormField') ?? content.postalCodeFormField,
    notFoundErrorNotification: findByRole(content.notifications, 'notFoundErrorNotification') ?? content.notFoundErrorNotification,
    serverErrorNotification: findByRole(content.notifications, 'serverErrorNotification') ?? content.serverErrorNotification,
  });
